// landing/FAQsSection.jsx
import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';

/* ========================
 * FAQItem - Displays a single FAQ item with a toggleable answer
 * Internal component for FAQsSection
 * ========================*/
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border border-white/10 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between bg-gray-900/30 hover:bg-gray-900/50 transition-colors text-left"
      >
        <span className="font-medium">{question}</span>
        <ChevronRight className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="px-6 py-4 text-gray-300 bg-black/20 animate-fade-in">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

/* ========================
 * FAQsSection - Displays the Frequently Asked Questions section
 * Contains a list of toggleable FAQ items (FAQItem defined internally)
 * ========================*/
const FAQsSection = () => {
  const faqData = [
    {
      question: "How does the pricing work?",
      answer: "Shelbula has 2 modes. Free gives you 3 local workspaces on one device. Cloud is $9/month and gives you unlimited cloud workspaces with secure sync, persistent files, history, and context that carry across everywhere you work. On both plans, you use your own API keys for models like OpenAI, Claude, and Gemini and pay those providers directly for usage."
    },
    {
      question: "What's the difference between local and cloud workspaces?",
      answer: "Local workspaces are private to the device you're using and are great for trying Shelbula on one device. Cloud workspaces are securely synced, persist across sessions, and let your files, memory, and history travel with you across devices."
    },
    {
      question: "What's the advantage over using LLM retail chats directly?",
      answer: "Retail chat apps give you isolated conversations inside a single provider's product. Shelbula gives you a private workspace where models, files, notes, memory, tools, and project context all live together. You're not locked into one provider, and your work carries forward instead of resetting every time you switch threads."
    },
    {
      question: "How is this different from open source UIs?",
      answer: "Unlike open source alternatives, Shelbula requires no self-hosting or repo setup, gives you a polished everyday workspace, and lets you start free before deciding whether you want secure cloud sync. You get the flexibility of a serious AI toolchain without the maintenance burden."
    },
    {
      question: "What is Bring Your Own Key (BYOK) and how do I get one?",
      answer: "BYOK means you connect your own external APIs from providers like OpenAI, Anthropic, Google, and others. Create an account with the provider you want, generate an API key in their developer console, and add it to Shelbula once. That keeps your usage portable, transparent, and under your control."
    },
    {
      question: "How is Shelbula different from ChatGPT, Claude.ai, or Gemini?",
      answer: "Those products are single-provider destinations. Shelbula is a private AI workspace that lets you use Claude, GPT, Gemini, Grok, Mistral, and OpenRouter models in one environment while layering on workspace memory, custom assistants, tool access, reusable context, and encrypted continuity across projects."
    },
    {
      question: "What's new in V5 compared to earlier versions?",
      answer: "V5 pushes Shelbula beyond a better chat surface and toward a true working environment. Workspaces are more central, tools and external APIs are more naturally integrated, and the product is being reframed around continuity, privacy, and project-based context instead of isolated conversations."
    },
    {
      question: "How does the memory feature work?",
      answer: "Shelbula builds a personal memory bank about your preferences over time. This information is securely stored and used to make future conversations more relevant and productive. You can even see what memories were considered per response!"
    },
    {
      question: "Is my data secure and private?",
      answer: "Yes. All your conversations are encrypted and stored securely. We don't train models on your data or share it with third parties. You maintain complete ownership of your information, and your API keys are stored in the cloud with full encryption - no human can access them."
    },
    {
      question: "What are workspaces?",
      answer: "Workspaces are the core unit inside Shelbula. Each workspace keeps its own files, context, memory, and history so your client project, research effort, or personal system can stay cleanly separated from everything else. Free includes 3 local workspaces. Cloud gives you unlimited synced workspaces."
    },
    {
      question: "What if I want to cancel?",
      answer: "You can cancel your subscription anytime with just a few clicks. There are no long-term commitments or cancellation fees. If you cancel, you'll maintain access until the end of your current billing period."
    }
  ];

  return (
    <section id="faqs" className="relative py-20 px-6 border-t border-white/5 z-10">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Frequently Asked Questions</h2>
        </div>
        
        <div className="space-y-3">
          {faqData.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQsSection;